import React from 'react';
import { Modal, Button, Row, Col, Badge } from 'react-bootstrap';
import { User, Mail, Phone, Shield, Calendar, MapPin, Building2 } from 'lucide-react';
import './UserDetailsModal.css';

const UserDetailsModal = ({ show, onHide, user }) => {
  if (!user) return null;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered className="user-details-modal">
      <Modal.Header closeButton>
        <Modal.Title>User Details</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="user-details">
          {/* User Header */}
          <div className="user-header mb-4">
            <div className="d-flex align-items-center">
              <div className="user-avatar me-3">
                <User size={48} />
              </div>
              <div>
                <h4 className="mb-1">{user.firstName} {user.lastName}</h4>
                <p className="text-muted mb-0">
                  {user.email ? (
                    <a 
                      href={`mailto:${user.email}`} 
                      style={{ color: '#0d6efd', textDecoration: 'none' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {user.email}
                    </a>
                  ) : (
                    <span className="text-muted">N/A</span>
                  )}
                </p>
                <div className="mt-2">
                  <Badge bg={user.isActive === true ? 'success' : 'secondary'} className="me-2">
                    {user.isActive === true ? 'Active' : 'Inactive'}
                  </Badge>
                  <Badge bg="light" text="dark">
                    {user.role || 'user'}
                  </Badge>
                </div>
              </div>
            </div>
          </div>

          {/* User Information */}
          <Row>
            <Col md={6}>
              <div className="info-section mb-3">
                <h6 className="text-muted mb-3">Personal Information</h6>
                <div className="info-item">
                  <div className="info-label">
                    <User size={16} className="me-2" />
                    Full Name
                  </div>
                  <div className="info-value">{user.firstName} {user.lastName}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">
                    <Mail size={16} className="me-2" />
                    Email Address
                  </div>
                  <div className="info-value">
                    {user.email ? (
                      <a 
                        href={`mailto:${user.email}`} 
                        style={{ color: '#0d6efd', textDecoration: 'none' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {user.email}
                      </a>
                    ) : (
                      <span className="text-muted">N/A</span>
                    )}
                  </div>
                </div>
                <div className="info-item">
                  <div className="info-label">
                    <Phone size={16} className="me-2" />
                    Department
                  </div>
                  <div className="info-value">{user.department || 'Not specified'}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">
                    <Building2 size={16} className="me-2" />
                    Company
                  </div>
                  <div className="info-value">
                    {user.company ? (
                      `${user.company.company} ${user.company.branch ? `- ${user.company.branch}` : ''}`
                    ) : user.assetId ? (
                      <span className="text-muted">Company not found</span>
                    ) : (
                      <span className="text-muted">No company assigned</span>
                    )}
                  </div>
                </div>
              </div>
            </Col>
            <Col md={6}>
              <div className="info-section mb-3">
                <h6 className="text-muted mb-3">Account Information</h6>
                <div className="info-item">
                  <div className="info-label">
                    <Shield size={16} className="me-2" />
                    User ID
                  </div>
                  <div className="info-value">{user.id}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">
                    <Shield size={16} className="me-2" />
                    Login
                  </div>
                  <div className="info-value">
                    {user.email ? (
                      <a 
                        href={`mailto:${user.email}`} 
                        style={{ color: '#0d6efd', textDecoration: 'none' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {user.email}
                      </a>
                    ) : (
                      <span className="text-muted">N/A</span>
                    )}
                  </div>
                </div>
                <div className="info-item">
                  <div className="info-label">
                    <Calendar size={16} className="me-2" />
                    Status
                  </div>
                  <div className="info-value">
                    <Badge bg={user.isActive === true ? 'success' : 'secondary'}>
                      {user.isActive === true ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </div>
                <div className="info-item">
                  <div className="info-label">
                    <Shield size={16} className="me-2" />
                    Locked Out
                  </div>
                  <div className="info-value">
                    <Badge bg={user.isLockedOut === true ? 'danger' : 'success'}>
                      {user.isLockedOut === true ? 'Yes' : 'No'}
                    </Badge>
                  </div>
                </div>
              </div>
            </Col>
          </Row>

          {/* Additional Information */}
          <div className="info-section">
            <h6 className="text-muted mb-3">Role & Permissions</h6>
            <div className="info-item">
              <div className="info-label">
                <Shield size={16} className="me-2" />
                Role
              </div>
              <div className="info-value">
                <Badge bg="light" text="dark" className="text-capitalize">
                  {user.role || 'user'}
                </Badge>
              </div>
            </div>
            <div className="info-item">
              <div className="info-label">
                <Shield size={16} className="me-2" />
                Account Type
              </div>
              <div className="info-value">User Account</div>
            </div>
            {user.bio && (
              <div className="info-item">
                <div className="info-label">
                  <User size={16} className="me-2" />
                  Bio
                </div>
                <div className="info-value">{user.bio}</div>
              </div>
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UserDetailsModal; 